import React from 'react';
import {
  Button, Modal, Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IoAlertCircleOutline, IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import {
  extractApiPayload,
  statusFromApiResponse,
} from 'screens/Admin/Accounts/components/syncSolicitudDocModalHelpers';

type SyncSolicitudDocResultModalProps = {
  show: boolean;
  accountId: number;
  terminalResponse: any;
  timedOut: boolean;
  runId: string | null;
  isRetrying?: boolean;
  onClose: () => void;
  onViewErrorLogs?: () => void;
  onRetry?: () => void;
};

function SyncSolicitudDocResultModal( {
  show,
  accountId,
  terminalResponse,
  timedOut,
  runId,
  isRetrying = false,
  onClose,
  onViewErrorLogs,
  onRetry,
}: SyncSolicitudDocResultModalProps ) {
  const { t } = useTranslation();

  const status = timedOut ? 'FAILED' : statusFromApiResponse( terminalResponse );
  const payload = terminalResponse ? extractApiPayload( terminalResponse ) : {};
  const lastMessage = String( payload.lastMessage ?? payload.LastMessage ?? '' );
  const errorCount = payload.errorCount ?? payload.ErrorCount;
  const apiFailed = Boolean( terminalResponse && !terminalResponse.ok );

  const isSuccess = status === 'COMPLETED';
  const isWarn = status === 'COMPLETED_WITH_ERRORS';
  const isFail = status === 'FAILED' || timedOut || apiFailed;

  let icon = <IoCheckmarkCircleOutline size={40} className="text--green2" />;
  let title = t( 'admin.account.syncSolicitudDocResultCompleted' );
  if ( timedOut ) {
    icon = <IoAlertCircleOutline size={40} className="text-warning" />;
    title = t( 'admin.account.syncSolicitudDocResultTimeout' );
  } else if ( isFail ) {
    icon = <IoAlertCircleOutline size={40} className="text-danger" />;
    title = t( 'admin.account.syncSolicitudDocResultFailed' );
  } else if ( isWarn ) {
    icon = <IoWarningOutline size={40} className="text-warning" />;
    title = t( 'admin.account.syncSolicitudDocResultCompletedWithErrors' );
  }

  const showLogsCta = ( isWarn || isFail ) && runId && onViewErrorLogs;
  const statusLabel = status || ( isSuccess ? 'COMPLETED' : '' );
  const showRetryCta = Boolean( onRetry );

  return (
    <Modal show={show} centered onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        <div className="mb-3 d-flex justify-content-center">{icon}</div>
        <p className="text-muted small mb-2">
          {t( 'admin.account.syncSolicitudDocAccountLabel' )}
          {' '}
          <strong>{accountId}</strong>
        </p>
        {runId ? (
          <p className="text-muted small mb-2">
            {t( 'admin.account.syncSolicitudDocRunIdLabel' )}
            {' '}
            <code>{runId}</code>
          </p>
        ) : null}
        {!timedOut && statusLabel ? (
          <p className="text--sm mb-2">
            <strong>{t( 'admin.account.syncSolicitudDocStatusLabel' )}</strong>
            {' '}
            {statusLabel}
          </p>
        ) : null}
        {errorCount != null && !timedOut ? (
          <p className="text--sm mb-2">
            {t( 'admin.account.syncSolicitudDocErrorCountLabel' )}
            {': '}
            {String( errorCount )}
          </p>
        ) : null}
        {lastMessage ? (
          <p className="text--sm text-start bg-light p-3 rounded mb-0">{lastMessage}</p>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-column gap-2">
        {showRetryCta ? (
          <Button
            variant="primary"
            className="w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={onRetry}
            disabled={isRetrying}
          >
            {isRetrying ? <Spinner animation="border" size="sm" /> : null}
            {t( 'admin.account.syncSolicitudDocSyncButton' )}
          </Button>
        ) : null}
        {showLogsCta ? (
          <Button variant="outline-primary" className="w-100" onClick={onViewErrorLogs}>
            {t( 'admin.account.syncSolicitudDocViewErrorLogs' )}
          </Button>
        ) : null}
        <Button variant="primary" className="w-100" onClick={onClose}>
          {t( 'close' )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SyncSolicitudDocResultModal;
